import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { FormControl, InputAdornment, MenuItem, Stack, Typography } from "@mui/material";
import { ErrorAlert } from "component/ErrorAlert";
import { FormDateTimePicker } from "component/FormDateTimePicker";
import { FormRadioGroup } from "component/FormRadioGroup";
import { FormSelect } from "component/FormSelect";
import { FormTextInput } from "component/FormTextInput";
import { AlignJustifyIcon, ChevronDownIcon, Dashboard3Icon, DoubleArrowUpIcon } from "component/Icons";
import {
  DEFAULT_TIME_ZONE,
  PLANNED_DEPARTURE_OPTIONS,
  PRIORITY_SELECT_ITEMS,
  RISK_SCORE_SELECT_ITEMS,
  SPECIAL_CONDITION_SELECT_ITEMS,
  MEDICAL_TEAM_OPTIONS,
} from "constant";
import { format } from "date-fns-tz";
import { enAU } from "date-fns/locale";
import { Controller, useForm } from "react-hook-form";
import {
  ActionFunction,
  LoaderFunction,
  redirect,
  useFetcher,
  useLoaderData,
  useRouteLoaderData,
} from "react-router-dom";
import { COLORS } from "style";
import {
  Hospital,
  Mission,
  MissionAssessmentConditionEnum,
  MissionMedicalTeamEnum,
  MissionPatientPriorityEnum,
  MissionPlannedDepartureEnum,
  MissionStatusEnum,
  MissionTaskingTypeEnum,
  User,
} from "type/model/api";
import { CreatePrimaryMissionFormProps, createPrimaryMissionSchema } from "type/schema";
import * as api from "api";
import { checkRoleLoader, pathWithSearchParams } from "utility/router";
import { CustomInputLabel } from "component/CustomInputLabel";
import { CustomSelect } from "component/CustomSelect";
import { UserRoles } from "type/model/view";
import { ErrorText } from "component/ErrorMessage";
import { getApiErrorMessage } from "utility/error-message";

export const createPrimaryMissionLoader: LoaderFunction = async (params) => {
  await checkRoleLoader([UserRoles.Tasker])(params);
  let hospitals: Hospital[] = [];
  try {
    const hospitalsQuery = await api.queryClient.ensureQueryData({
      queryKey: ["hospitals"],
      queryFn: api.fetchHospitals,
    });
    hospitals = hospitalsQuery.data;
  } catch {}

  return { hospitals };
};

export const createPrimaryMissionAction: ActionFunction = async ({ request }) => {
  const formValues = await request.formData();
  const missionValues = formValues.get("mission");
  if (missionValues && typeof missionValues === "string") {
    try {
      const response = await api.createMission({ mission: JSON.parse(missionValues) });
      const missionId = response.data.data.id;
      return redirect(pathWithSearchParams(`/main/dashboard/mission/${missionId}/recommendations`));
    } catch (error) {
      return { formError: getApiErrorMessage(error) };
    }
  } else {
    return { formError: "Invalid form data format!" };
  }
};

export const CreatePrimaryMission = () => {
  const { hospitals } = useLoaderData() as { hospitals: Hospital[] };
  const { currentUser } = useRouteLoaderData("main") as { currentUser: User };
  const currentUserTimeZone = currentUser.time_zone || DEFAULT_TIME_ZONE;
  const currentUserTimeZoneAbbreviation = format(Date.now(), "zzz", {
    timeZone: currentUserTimeZone,
    locale: enAU,
  });

  const fetcher = useFetcher();
  const { handleSubmit, control, watch } = useForm<Partial<CreatePrimaryMissionFormProps>>({
    resolver: zodResolver(createPrimaryMissionSchema),
    defaultValues: {
      plannedDeparture: MissionPlannedDepartureEnum.Now,
      medicalTeam: MissionMedicalTeamEnum.FlightNurseOnly,
      specialCondition: MissionAssessmentConditionEnum.TimeToPatientCritical,
      priority: MissionPatientPriorityEnum.P1Immediate,
    },
    mode: "all",
  });

  const plannedDeparture = watch("plannedDeparture");

  const onSubmit = (values: Partial<CreatePrimaryMissionFormProps>) => {
    const formData = new FormData();
    const transformedData: Partial<Mission> = {
      status: MissionStatusEnum.Draft,
      is_multipatient: false,
      tasking_type: MissionTaskingTypeEnum.Primary,
      aviation_risk: values.riskScore,
      assessment_condition: values.specialCondition,
      is_winch_required: values.isWinchRequired === "true",
      planned_departure: values.plannedDeparture,
      planned_departure_at:
        values.departureTime && values.plannedDeparture !== "now"
          ? values.departureTime.toISOString()
          : values.plannedDeparture,
      //@ts-ignore
      tasks_attributes: [
        {
          position: "1",
          //@ts-ignore
          task: "patient_transport",
          to_hospital_id: values.destinationHospital,
          from_lat: values.latPickup,
          from_long: values.longPickup,
          patient_priority: values.priority,
          requires_doctor: values.medicalTeam === MissionMedicalTeamEnum.DoctorRequired,
        },
      ],
    };
    formData.append("mission", JSON.stringify(transformedData));

    fetcher.submit(formData, {
      action: "/main/dashboard/mission/create/primary",
      method: "post",
    });
  };

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ gap: 2 }}>
      <Stack
        sx={{
          flexGrow: 1,
          p: 4,
          border: `2px solid ${COLORS.WHITE_SMOKE}`,
          width: "100%",
          borderRadius: "20px",
          borderTop: 0,
          borderStartStartRadius: 0,
          borderStartEndRadius: 0,
        }}
      >
        <Stack sx={{ width: "100%", maxWidth: 720, marginLeft: "10%", gap: 4, alignItems: "flex-start" }}>
          <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
            <Controller
              name="priority"
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormSelect
                  label="Priority"
                  required
                  placeholder="Select a priority"
                  displayEmpty={true}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={error?.message}
                  sx={{ minWidth: 350 }}
                  startAdornment={
                    <InputAdornment position="start">
                      <DoubleArrowUpIcon />
                    </InputAdornment>
                  }
                  items={PRIORITY_SELECT_ITEMS}
                />
              )}
            />

            <Controller
              name="riskScore"
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormSelect
                  label="Risk score"
                  placeholder="Select a risk score"
                  displayEmpty={true}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={error?.message}
                  sx={{ minWidth: 350 }}
                  startAdornment={
                    <InputAdornment position="start">
                      <Dashboard3Icon />
                    </InputAdornment>
                  }
                  items={RISK_SCORE_SELECT_ITEMS}
                />
              )}
            />
          </Stack>
          <Controller
            name="specialCondition"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormSelect
                label="Special conditions"
                placeholder="Select a mission condition"
                displayEmpty={true}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                sx={{ minWidth: 350 }}
                startAdornment={
                  <InputAdornment position="start">
                    <AlignJustifyIcon />
                  </InputAdornment>
                }
                items={SPECIAL_CONDITION_SELECT_ITEMS}
              />
            )}
          />
          <Controller
            name="isWinchRequired"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormRadioGroup
                fullWidth
                required
                row
                label="Winch required"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                items={[
                  { label: "Yes", value: "true" },
                  { label: "No", value: "false" },
                ]}
              />
            )}
          />
          <Controller
            name="latPickup"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormTextInput
                required
                fullWidth
                label="Pickup Latitude"
                placeholder="Enter latitude"
                sx={{ minWidth: 350 }}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                type={"text"}
              />
            )}
          />
          <Controller
            name="longPickup"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormTextInput
                required
                fullWidth
                label="Pickup Longitude"
                placeholder="Enter longitude"
                sx={{ minWidth: 350 }}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                type={"text"}
              />
            )}
          />
          <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
            <Controller
              name="destinationHospital"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl>
                  <CustomInputLabel sx={{ marginBottom: "8px" }} required>
                    Likely destination hospital
                  </CustomInputLabel>
                  <CustomSelect
                    placeholder="Select a destination hospital"
                    displayEmpty={true}
                    renderValue={(value) => {
                      const hospital = hospitals.find((hospital) => hospital.id === value);
                      return hospital ? hospital.name : "Select a hospital";
                    }}
                    {...field}
                    size="small"
                    sx={{ minWidth: 350 }}
                    variant="outlined"
                    IconComponent={ChevronDownIcon}
                    MenuProps={{
                      disableAutoFocusItem: true,
                      MenuListProps: { sx: { bgcolor: COLORS.DR_WHITE } },
                    }}
                  >
                    {hospitals.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                  {error ? (
                    <Stack sx={{ mt: 1 }}>
                      <ErrorText>{error.message}</ErrorText>
                    </Stack>
                  ) : null}
                </FormControl>
              )}
            />
          </Stack>
          <Stack sx={{ gap: 1 }}>
            <Typography variant="h2">Logistics</Typography>
            <Typography>Select the relevant mission times</Typography>
          </Stack>
          <Controller
            name="plannedDeparture"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => {
              return (
                <FormRadioGroup
                  fullWidth
                  row
                  label="Planned departure"
                  required
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={error?.message}
                  items={PLANNED_DEPARTURE_OPTIONS}
                />
              );
            }}
          />
          {plannedDeparture === MissionPlannedDepartureEnum.SpecificTime ? (
            <Controller
              name="departureTime"
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormDateTimePicker
                  icon="clock"
                  label={`Departure time (${currentUserTimeZoneAbbreviation})`}
                  required
                  placeholder="Enter the departing time"
                  sx={{ width: 350 }}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  error={error?.message}
                />
              )}
            />
          ) : null}
          <Stack sx={{ gap: 1 }}>
            <Typography variant="h2">Medical team</Typography>
            <Typography>Select the medical team members</Typography>
          </Stack>
          <Controller
            name="medicalTeam"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormRadioGroup
                fullWidth
                row
                label="Medical team"
                required
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                items={MEDICAL_TEAM_OPTIONS}
              />
            )}
          />
        </Stack>
      </Stack>

      <Stack direction="row" sx={{ alignItems: "flex-start", justifyContent: "flex-end", gap: 4 }}>
        {fetcher.data?.formError ? <ErrorAlert>{fetcher.data.formError}</ErrorAlert> : null}

        <LoadingButton
          type="submit"
          loading={fetcher.state === "loading" || fetcher.state === "submitting"}
          variant="contained"
          sx={{ whiteSpace: "nowrap" }}
        >
          Next
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
